import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, Check, Loader2 } from "lucide-react";
import { trackOnboardingCompleted } from "@/analytics/analytics";
import { updateMyProfile } from "@/services/profile";
import { generateRecruitingPlan } from "@/services/pipeline";
import iconImg from "@/assets/icon.png";
import { useBranding } from "@/hooks/useBranding";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ONBOARDING_STEPS } from "./steps";
import { OnboardingBrand } from "./OnboardingBrand";
import type {
  OnboardingOutcome,
  OnboardingProfile,
  OnboardingProgress,
  OnboardingSaveFn,
  OnboardingStep,
  OnboardingWizardData,
} from "./types";

export interface OnboardingWizardProps {
  /** Current user profile — resume position is derived from saved fields. */
  profile: OnboardingProfile | null;
  /** Forwarded to steps; fired when a JGS profile is linked. */
  onLinkSuccess: (profileId: string) => void | Promise<void>;
  /** Fired once onboarding is fully complete (after onboardingComplete is saved). */
  onComplete?: () => void | Promise<void>;
  /** Fired when the flow reaches a terminal state (so the modal can close). */
  onFlowDone?: () => void;
  /** Close the full-screen flow without marking onboarding complete. */
  onExit?: () => void;
  /** Injectable for tests; defaults to the shared registry. */
  steps?: OnboardingStep[];
  onboardingVersion?: number;
  progress?: OnboardingProgress | null;
  exitLabel?: string;
  forceReview?: boolean;
}

/** Full-screen, resumable onboarding with one coach-led question stage at a time. */
export default function OnboardingWizard({
  profile,
  onLinkSuccess,
  onComplete,
  onFlowDone,
  onExit,
  steps = ONBOARDING_STEPS,
  onboardingVersion = 2,
  progress,
  exitLabel = "Save & exit",
  forceReview = false,
}: OnboardingWizardProps) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const coachIcon = branding?.chatIdleIconUrl || iconImg;

  const [profilePatch, setProfilePatch] = useState<Partial<OnboardingProfile>>({});
  const updateProfile = useCallback((patch: Partial<OnboardingProfile>) => {
    setProfilePatch(previous => ({ ...previous, ...patch }));
  }, []);
  const safeProfile: OnboardingProfile = { ...(profile ?? {}), ...profilePatch };

  const initialIndex = useMemo(() => {
    if (forceReview) return 0;
    if (progress?.version === onboardingVersion && progress.currentStepId) {
      const savedIndex = steps.findIndex(step => step.id === progress.currentStepId);
      if (savedIndex >= 0) return savedIndex;
    }
    // A current-version flow without a checkpoint can still use saved answers.
    const firstIncomplete = steps.findIndex(step => !step.isComplete(safeProfile));
    return firstIncomplete === -1 ? steps.length - 1 : firstIncomplete;
    // Resume position is intentionally derived once from the mount-time profile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [index, setIndex] = useState(initialIndex);
  const [stepValid, setStepValid] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishingStage, setFinishingStage] = useState<"profile" | "schools" | "ready">("profile");
  const [finishError, setFinishError] = useState<string | null>(null);

  const saveRef = useRef<OnboardingSaveFn | null>(null);
  const finishingRef = useRef(false);
  const [wizardData, setWizardDataState] = useState<OnboardingWizardData>({
    dreamSchools: progress?.draft?.dreamSchools ?? [],
    recruitingProgress: progress?.draft?.recruitingProgress ?? [],
  });
  const wizardDataRef = useRef<OnboardingWizardData>(wizardData);
  const completedStepIdsRef = useRef<string[]>(progress?.completedStepIds ?? []);

  const setWizardData = useCallback((patch: Partial<OnboardingWizardData>) => {
    wizardDataRef.current = { ...wizardDataRef.current, ...patch };
    setWizardDataState(wizardDataRef.current);
  }, []);

  const saveProgress = useCallback(
    async (completedStepId: string, currentStepId: string | null) => {
      completedStepIdsRef.current = [...new Set([...completedStepIdsRef.current, completedStepId])];
      await updateMyProfile({
        onboardingVersion,
        onboardingStepId: currentStepId,
        onboardingCompletedStepIds: completedStepIdsRef.current,
        onboardingDraft: wizardDataRef.current,
      });
    },
    [onboardingVersion]
  );

  const registerSave = useCallback((save: OnboardingSaveFn | null) => {
    saveRef.current = save;
  }, []);

  const onValidityChange = useCallback((valid: boolean) => {
    setStepValid(valid);
  }, []);

  const step = steps[index];
  const isLastStep = index === steps.length - 1;
  const StepComponent = step.Component;

  const finish = useCallback(
    async (outcome: OnboardingOutcome) => {
      if (finishingRef.current) return;
      finishingRef.current = true;
      setFinishing(true);
      setFinishingStage("profile");
      setFinishError(null);

      try {
        await updateMyProfile({
          onboardingVersion,
          onboardingStepId: step.id,
          onboardingCompletedStepIds: steps.map(item => item.id),
          onboardingDraft: wizardDataRef.current,
        });
      } catch {
        finishingRef.current = false;
        setFinishError("Your completed profile could not be saved.");
        return;
      }

      const { dreamSchools, recruitingProgress } = wizardDataRef.current;
      setFinishingStage("schools");
      let portfolioSize = 0;
      try {
        const result = await generateRecruitingPlan({ dreamSchools, recruitingProgress });
        portfolioSize = result.portfolioSize ?? result.added;
        const portfolioReady =
          result.added > 0 ||
          (result.portfolioSize ?? 0) > 0 ||
          result.reason === "portfolio_target_reached";
        if (!portfolioReady) throw new Error(result.reason || "school_list_empty");
      } catch {
        finishingRef.current = false;
        setFinishError(
          "Your profile is saved, but your school list could not be generated. Nothing was lost."
        );
        return;
      }

      // Only mark onboarding complete after the user's initial school list is
      // confirmed. If generation fails (or the page reloads mid-generation),
      // the onboarding modal can resume this final step instead of sending the
      // user to an empty My Schools page.
      try {
        await updateMyProfile({
          onboardingComplete: true,
          onboardingVersion,
          onboardingStepId: null,
          onboardingCompletedStepIds: steps.map(item => item.id),
          onboardingDraft: wizardDataRef.current,
        });
      } catch {
        finishingRef.current = false;
        setFinishError("Your school list is ready, but onboarding could not be finalized.");
        return;
      }

      setFinishingStage("ready");

      trackOnboardingCompleted({ outcome, destination: "my_schools" });
      onFlowDone?.();
      await onComplete?.();
      navigate("/pipeline", {
        state: { initialPortfolioSetup: true, portfolioSize },
      });
    },
    [navigate, onComplete, onFlowDone, onboardingVersion, step.id, steps]
  );

  const goForward = useCallback(
    async (outcome?: OnboardingOutcome) => {
      if (isLastStep) {
        void finish(outcome ?? "skipped");
        return;
      }

      try {
        await saveProgress(step.id, steps[index + 1]?.id ?? null);
      } catch (error) {
        toast.error("Your progress could not be saved. Please try again.");
        return;
      }

      saveRef.current = null;
      setStepValid(false);
      setIndex(current => current + 1);
    },
    [finish, index, isLastStep, saveProgress, step.id, steps]
  );

  const handleContinue = useCallback(async () => {
    const save = saveRef.current;
    if (save) {
      const saved = await save();
      if (!saved) return;
    }
    await goForward();
  }, [goForward]);

  const handleSkip = useCallback(() => {
    saveRef.current = null;
    void goForward("skipped");
  }, [goForward]);

  const handleBack = useCallback(() => {
    if (index === 0) return;
    saveRef.current = null;
    setStepValid(false);
    setIndex(current => current - 1);
  }, [index]);

  if (finishing) {
    const stages = [
      { id: "profile", label: "Saving your recruiting profile" },
      { id: "schools", label: "Building a broad starting list" },
      { id: "ready", label: "Preparing Golf Fit assessments" },
    ] as const;
    const stageIndex = stages.findIndex(item => item.id === finishingStage);

    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface px-5 text-center">
        <div className="w-full max-w-lg">
          <div className="mx-auto mb-6 h-24 w-24 overflow-hidden rounded-full border-4 border-white bg-black shadow-[0_18px_50px_rgba(25,58,43,0.14)]">
            <img
              src={coachIcon}
              alt="golfrecruit.ai coach"
              className="h-full w-full object-cover"
            />
          </div>
          <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-primary-text">
            Your answers are in
          </p>
          <h2 className="text-4xl tracking-[-0.04em] text-forest sm:text-5xl">
            {finishError ? "Your list needs another try." : "Building your starting school list…"}
          </h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
            {finishError
              ? finishError
              : "golfrecruit.ai will build up to 50 Dream, Realistic, and Safety programs. We recommend that you start wide and narrow your list over time."}
          </p>
          <div className="mx-auto mt-7 max-w-sm space-y-3 text-left">
            {stages.map((item, itemIndex) => (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-bold text-forest"
              >
                {finishError && item.id === finishingStage ? (
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                ) : itemIndex === stageIndex ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary-text" />
                ) : itemIndex < stageIndex ? (
                  <Check className="h-5 w-5 text-primary-text" />
                ) : (
                  <span className="h-5 w-5 rounded-full border-2 border-border" />
                )}
                {item.label}
              </div>
            ))}
          </div>
          {finishError && (
            <button
              type="button"
              onClick={() => void finish("completed")}
              className="mt-6 min-h-12 rounded-2xl border-2 border-primary bg-primary px-7 text-sm font-extrabold text-primary-foreground shadow-[0_4px_0_#3c7c24] transition hover:-translate-y-0.5 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber/70"
            >
              Try generating again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-surface text-forest">
      <header className="grid min-h-[70px] shrink-0 grid-cols-[42px_1fr_auto] items-center gap-3 border-b border-forest/10 bg-white/90 px-4 py-3 backdrop-blur sm:grid-cols-[180px_minmax(240px,520px)_180px] sm:gap-7 sm:px-8 sm:py-4">
        <OnboardingBrand className="hidden sm:inline-flex" />

        <div className="col-span-2 flex items-center gap-3 sm:col-span-1">
          <button
            type="button"
            aria-label="Back"
            onClick={handleBack}
            disabled={index === 0}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border bg-white text-forest transition hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-35"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex flex-1 gap-1.5" aria-label={`Step ${index + 1} of ${steps.length}`}>
            {steps.map((registeredStep, stepIndex) => (
              <span
                key={registeredStep.id}
                className={cn(
                  "h-1.5 flex-1 rounded-full bg-lime-soft",
                  stepIndex <= index && "bg-primary",
                  stepIndex === index && "shadow-[0_0_0_3px_rgba(103,198,47,0.13)]"
                )}
              />
            ))}
          </div>
          <span className="hidden whitespace-nowrap font-mono text-[11px] font-semibold tracking-wide text-muted-foreground sm:block">
            {index + 1} OF {steps.length}
          </span>
          <span className="sr-only">{step.title}</span>
        </div>

        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="justify-self-end text-[11px] font-bold text-muted-foreground transition-colors hover:text-forest sm:text-xs"
          >
            {exitLabel}
          </button>
        )}
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <StepComponent
            key={step.id}
            profile={safeProfile}
            onValidityChange={onValidityChange}
            registerSave={registerSave}
            onLinkSuccess={onLinkSuccess}
            onStepComplete={goForward}
            setWizardData={setWizardData}
            wizardData={wizardData}
            updateProfile={updateProfile}
          />
        </div>
      </main>

      {!step.managesOwnNavigation && (
        <footer className="shrink-0 border-t border-forest/10 bg-white/95 px-4 py-3 backdrop-blur sm:py-4">
          <div className="mx-auto flex w-full max-w-[700px] items-center justify-end gap-3">
            {step.canSkip && (
              <button
                type="button"
                onClick={handleSkip}
                className="min-h-12 px-3 text-xs font-extrabold text-muted-foreground transition-colors hover:text-forest sm:px-5 sm:text-sm"
              >
                Skip for now
              </button>
            )}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!stepValid}
              className="min-h-12 min-w-[190px] flex-1 rounded-2xl border-2 border-primary bg-primary px-6 text-sm font-extrabold text-primary-foreground shadow-[0_4px_0_#3c7c24] transition hover:-translate-y-0.5 hover:bg-primary/90 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-subtle/60 disabled:shadow-[0_4px_0_#c8d3ca] disabled:hover:translate-y-0 sm:flex-none sm:min-w-[220px]"
            >
              {step.continueLabel ?? "Continue"}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
